import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { ArrowRight, Gem, Briefcase, Truck, Star } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Logo from '@/components/Logo';

const Home = () => {

  const features = [
    {
      icon: <Gem className="w-10 h-10 text-primary" />,
      title: "A World Beyond the Catalogue",
      description: "Design Vault opens the door to a rarefied world of exceptional furnishings and distinguished brands width access typically reserved for a privileged inner circle. Each piece is curated with discernment, intention, and respect for enduring design."
    },
    {
      icon: <Briefcase className="w-10 h-10 text-primary" />,
      title: "The Art of Effortless Command",
      description: "Projects unfold within a single, intelligent ecosystem where inspiration, specifications, budgets, and dialogue converge. What was once fragmented becomes fluid—allowing clarity, control, and creativity to coexist seamlessly."
    },
    {
      icon: <Truck className="w-10 h-10 text-primary" />,
      title: "Procurement, Perfected",
      description: "From the first inquiry to final delivery, every logistical complexity is quietly mastered on your behalf. Our role is invisible yet indispensable, ensuring each project moves forward with precision, discretion, and unwavering confidence."
    }
  ];
  
  const testimonials = [
      {
          quote: "Design Vault has transformed how we source for our high-end residential projects. The curated selection is unparalleled.",
          name: "Eleanor Vance",
          title: "Principal Designer, Vance & Co."
      },
      {
          quote: "The project management tools are a game-changer. What used to take days of admin now takes hours. A must-have for any serious firm.",
          name: "Julian Adler",
          title: "Founder, Adler Architects"
      }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.3 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 100 }
    }
  };

  return (
    <>
      <Helmet>
        <title>Design Vault - The Toolbox for Design Professionals</title>
        <meta name="description" content="Discover Design Vault, the B2B platform that combines exclusive sourcing, powerful project management tools, and a concierge service for architects and interior designers." />
      </Helmet>
      
      <div className="w-full bg-background text-foreground">
        {/* Hero Section */}
        <motion.section 
          className="relative text-center py-32 md:py-48 px-4 overflow-hidden bg-card"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-card via-card/80 to-transparent z-10"></div>
          <div className="absolute inset-0 z-0">
             <img
                className="object-cover w-full h-full"
                alt="A luxurious and modern living room interior with designer sofas and abundant natural light" src="https://inspiration.designvault.ca/home/AdobeStock_319880408.png" />
          </div>
          
          <motion.div 
            className="relative z-20 max-w-4xl mx-auto"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants} className="flex justify-center mb-8">
              <Logo className="h-64 md:h-96 lg:h-[32rem]" />
            </motion.div>
            <motion.h1 
              variants={itemVariants}
              className="text-4xl md:text-6xl font-bold tracking-tight text-foreground" 
              style={{textShadow: '0 2px 20px rgba(255,255,255,0.7)'}}
            >
              Trade-exclusive furniture procurement, rooted in the Okanagan
            </motion.h1>
            <motion.p 
              variants={itemVariants}
              className="mt-6 text-lg md:text-xl max-w-3xl mx-auto text-foreground/90"
               style={{textShadow: '0 1px 10px rgba(255,255,255,0.7)'}}
            >
              While The Design Vault oversees sourcing, order management, custom tariffs, delivery and service coordination, you are free to focus on what matters most: creativity and design. We simplify the complexities behind the scenes, allowing your vision to take center stage. Let The Design Vault become your discreet, trusted assistant.
            </motion.p>
            <motion.div 
              variants={itemVariants}
              className="mt-12 flex flex-col sm:flex-row justify-center gap-4"
            >
              <Button asChild size="lg" className="text-lg py-7 px-8 shadow-lg shadow-primary/20">
                <NavLink to="/request-access">
                  Request Pro Access <ArrowRight className="ml-2 h-5 w-5" />
                </NavLink>
              </Button>
            </motion.div>
            <motion.p
              variants={itemVariants}
              className="mt-6 text-lg md:text-xl max-w-3xl mx-auto text-foreground/90"
              style={{textShadow: '0 1px 10px rgba(255,255,255,0.7)'}}
            ><b>An unparalleled collection of mid- to high-end furniture sourced from leading manufacturers around the world, offered at highly competitive professional pricing—exclusively for the trade.</b> 
            </motion.p>
          </motion.div>
        </motion.section>

        {/* Features Section */}
        <section className="py-24 bg-secondary">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-sm font-bold uppercase tracking-wider text-primary">THE ALL-IN-ONE PLATFORM</h2>
              <p className="mt-4 text-4xl font-bold tracking-tight text-foreground">Crafted for design professionnals, locally based.</p>
              <p className="mt-5 text-lg text-muted-foreground">More than a catalogue, Design Vault is a strategic procurement partner streamlining sourcing, elevating the client experience, and enabling thoughtful, sustainable growth.</p>
            </div>
            
            <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                <motion.div 
                  className="grid grid-cols-1 gap-10"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                >
                  {features.map((feature, index) => (
                    <motion.div key={index} variants={itemVariants} className="flex items-start gap-6">
                      <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-lg bg-primary/10 mt-1">
                        {feature.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-foreground">{feature.title}</h3>
                        <p className="mt-2 text-muted-foreground">{feature.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
                <motion.div 
                    className="h-full w-full rounded-lg overflow-hidden"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <img className="object-cover w-full h-full min-h-[400px]" alt="A stylish office interior with designer furniture and a large window" src="https://inspiration.designvault.ca/home/238754-003_ESS_1.jpg" />
                </motion.div>
            </div>
          </div>
        </section>

        {/* Inspiration Gallery */}
        <section className="py-24 bg-card">
            <div className="container mx-auto px-4 text-center">
                <h2 className="text-4xl font-bold tracking-tight text-foreground">Where furniture sourcing meets precision</h2>
                <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">Trade professionals simply describe what they are looking for—or share an image—through our intuitive portal. Within one business day, they receive curated options with transparent pricing. Because we understand that time efficiency is essential to our clients’ success</p>
                <motion.div 
                    className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                >
                    <motion.div variants={itemVariants} className="col-span-2 row-span-2 rounded-lg overflow-hidden">
                        <img className="w-full h-full object-cover" alt="Spacious living room with minimalist decor" src="https://inspiration.designvault.ca/home/105763-007_ROM_1.jpg" />
                    </motion.div>
                    <motion.div variants={itemVariants} className="rounded-lg overflow-hidden">
                        <img className="w-full h-full object-cover" alt="Designer armchair in a cozy corner" src="https://inspiration.designvault.ca/home/231363-001_ESS.jpg" />
                    </motion.div>
                    <motion.div variants={itemVariants} className="rounded-lg overflow-hidden">
                        <img className="w-full h-full object-cover" alt="Elegant dining room setup" src="https://inspiration.designvault.ca/home/VTUD-05-10_ESS_1.jpg" />
                    </motion.div>
                    <motion.div variants={itemVariants} className="col-span-2 rounded-lg overflow-hidden">
                        <img className="w-full h-full object-cover" alt="Luxury hotel lobby with modern lighting" src="https://inspiration.designvault.ca/home/249200-002_ESS.jpg" />
                    </motion.div>
                </motion.div>
            </div>
        </section>
        
        
         {/* Call to Action Section */}
        <section className="relative py-32 px-4 bg-primary/90 text-primary-foreground">
           <div className="absolute inset-0 z-0 opacity-10">
             <img
                className="object-cover w-full h-full"
                alt="Close-up of luxurious fabric and material samples on a table" src="https://inspiration.designvault.ca/home/241461-001_ESS.jpg" />
          </div>
          <div className="relative z-10 text-center max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold tracking-tight">Elevate your workflow</h2>
            <p className="mt-4 text-lg text-primary-foreground/80">Gain access to a private professional portal where projects are created and managed seamlessly. Quotes include images, detailed descriptions, and transparent pricing—and can be converted directly into invoices. All communication is centralized within the portal, eliminating fragmented emails through an intuitive, streamlined system designed for the way professionals work.</p>
            <div className="mt-10">
              <Button asChild size="lg" variant="secondary" className="text-lg py-7 px-8 shadow-2xl">
                <NavLink to="/login">
                  Login or Create Account
                </NavLink>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;